import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, DollarSign, Calendar, Briefcase, Loader, LogOut, Mail, FileText } from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const CancellationPaymentBlockerModal = ({ isOpen, outstandingFees }) => {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const [processingInvoiceId, setProcessingInvoiceId] = useState(null);

  if (!isOpen || !outstandingFees?.has_outstanding_fees) return null;

  const invoices = outstandingFees.invoices || [];
  const totalOutstanding = outstandingFees.total_outstanding || 0;

  const formatCurrency = (amount) => {
    return `$${(amount || 0).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      return new Date(dateStr).toLocaleDateString('en-AU', {
        day: 'numeric', month: 'short', year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  const handlePayNow = async (invoiceId) => {
    setProcessingInvoiceId(invoiceId);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/cancellation-invoices/${invoiceId}/create-checkout`
      );
      if (response.data.success && response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        toast.error('Failed to create payment session. Please try again.');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error(error.response?.data?.detail || 'Failed to initiate payment. Please contact support.');
    } finally {
      setProcessingInvoiceId(null);
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate('/login');
  };

  return (
    <div
      className="fixed inset-0 flex items-center justify-center z-[9999]"
      style={{ background: 'rgba(8,11,20,.7)', backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)', padding: 20 }}
    >
      <div
        className="relative w-full max-w-lg"
        style={{ background: '#12162A', border: '1px solid rgba(255,255,255,.12)', borderRadius: 22, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 40px 90px -30px rgba(0,0,0,.85)', fontFamily: "'Work Sans', sans-serif" }}
      >
        {/* Header - NO close button */}
        <div
          className="px-6 py-6 relative"
          style={{ background: 'linear-gradient(120deg,#B91C1C,#7F1D1D)', borderTopLeftRadius: 22, borderTopRightRadius: 22, overflow: 'hidden' }}
        >
          <img
            src="/assets/website/symbol-white.svg"
            alt=""
            aria-hidden="true"
            style={{ position: 'absolute', right: -24, top: -24, width: 130, height: 130, opacity: 0.06, animation: 'szspin 80s linear infinite', pointerEvents: 'none' }}
          />
          <div className="flex items-center gap-3 text-white relative">
            <div className="flex items-center justify-center" style={{ padding: 12, borderRadius: '9999px', background: 'rgba(255,255,255,.18)' }}>
              <AlertTriangle size={32} />
            </div>
            <div>
              <h2 className="text-2xl font-bold" style={{ fontFamily: "'Poppins', sans-serif" }}>Outstanding Payment Required</h2>
              <p className="text-sm mt-1" style={{ color: 'rgba(255,255,255,.8)' }}>Your platform access is restricted</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6" style={{ color: '#fff' }}>
          <div className="space-y-5">
            {/* Explanation */}
            <p className="text-sm" style={{ color: 'rgba(255,255,255,.6)' }}>
              You have an outstanding cancellation fee that must be paid before you can access the Shootzu platform.
              This fee was applied in accordance with the Shootzu Terms and Conditions.
            </p>

            {/* Invoice Cards */}
            {invoices.map((invoice) => (
              <div
                key={invoice.id}
                className="overflow-hidden"
                style={{ borderRadius: 12, border: '1px solid rgba(185,28,28,.45)', background: 'rgba(185,28,28,.16)' }}
              >
                <div className="px-4 py-2" style={{ background: 'rgba(185,28,28,.28)' }}>
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold uppercase" style={{ color: '#F87171' }}>
                      Cancellation Fee Invoice
                    </span>
                    <span className="text-xs" style={{ color: '#F87171', fontFamily: "'Roboto Mono', monospace" }}>
                      {invoice.invoice_number}
                    </span>
                  </div>
                </div>
                <div className="p-4 space-y-3">
                  <div className="flex items-start gap-2">
                    <Briefcase className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: 'rgba(255,255,255,.5)' }} />
                    <div>
                      <p className="text-sm font-medium" style={{ color: '#fff' }}>
                        {invoice.job_title}
                      </p>
                      <p className="text-xs" style={{ color: 'rgba(255,255,255,.5)' }}>
                        Job Date: {formatDate(invoice.job_date)}
                      </p>
                    </div>
                  </div>

                  {/* Fee Breakdown */}
                  <div className="p-3" style={{ borderRadius: 11, background: 'rgba(255,255,255,.04)', border: '1px solid rgba(255,255,255,.1)' }}>
                    <div className="space-y-1 text-xs">
                      <div className="flex justify-between">
                        <span style={{ color: 'rgba(255,255,255,.5)' }}>Cancellation Fee (7%):</span>
                        <span style={{ color: 'rgba(255,255,255,.8)' }}>{formatCurrency(invoice.fee_amount_ex_gst)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span style={{ color: 'rgba(255,255,255,.5)' }}>GST (10%):</span>
                        <span style={{ color: 'rgba(255,255,255,.8)' }}>{formatCurrency(invoice.gst_amount)}</span>
                      </div>
                      <div className="flex justify-between font-bold text-sm pt-1" style={{ borderTop: '1px solid rgba(255,255,255,.12)', color: '#F87171' }}>
                        <span>Total Due:</span>
                        <span>{formatCurrency(invoice.total_amount_inc_gst)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Pay Now Button */}
                  <Button
                    onClick={() => handlePayNow(invoice.id)}
                    disabled={processingInvoiceId === invoice.id}
                    className="w-full font-semibold text-white hover:opacity-90"
                    style={{ background: '#B91C1C' }}
                  >
                    {processingInvoiceId === invoice.id ? (
                      <>
                        <Loader className="w-4 h-4 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <DollarSign className="w-4 h-4 mr-2" />
                        Pay Now - {formatCurrency(invoice.total_amount_inc_gst)}
                      </>
                    )}
                  </Button>
                </div>
              </div>
            ))}

            {/* Total Outstanding */}
            {invoices.length > 1 && (
              <div className="p-4" style={{ borderRadius: 12, background: 'rgba(185,28,28,.28)', border: '1px solid rgba(185,28,28,.45)' }}>
                <div className="flex items-center justify-between">
                  <span className="font-semibold" style={{ color: '#F87171' }}>Total Outstanding:</span>
                  <span className="text-xl font-bold" style={{ color: '#F87171', fontFamily: "'Poppins', sans-serif" }}>
                    {formatCurrency(totalOutstanding)}
                  </span>
                </div>
              </div>
            )}

            {/* Help Section */}
            <div className="p-4" style={{ borderRadius: 12, background: 'rgba(255,255,255,.04)', border: '1px solid rgba(255,255,255,.1)' }}>
              <p className="text-xs" style={{ color: 'rgba(255,255,255,.5)' }}>
                If you believe this fee has been applied in error, or if you need assistance, please contact our support team.
              </p>
              <a
                href="mailto:hello@shootzu.com"
                className="inline-flex items-center gap-1 text-xs font-medium mt-2"
                style={{ color: '#28C0D0' }}
              >
                <Mail className="w-3 h-3" />
                hello@shootzu.com
              </a>
            </div>

            {/* Logout Button */}
            <button
              onClick={handleLogout}
              className="w-full flex items-center justify-center gap-2 py-2 text-sm transition-colors"
              style={{ borderRadius: 10, background: 'transparent', border: '1px solid rgba(255,255,255,.14)', color: 'rgba(255,255,255,.6)' }}
            >
              <LogOut className="w-4 h-4" />
              Log Out
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CancellationPaymentBlockerModal;
